import { Setting, SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';
import { OptionalDeep } from 'ts-toolbelt/out/Object/Optional';
import { DEFAULT_SETTINGS } from '../constants';
import { SettingTypeMap } from '../types';
import { convertSettingTypeToKey } from './convert-setting-type-to-key';

export const convertPartialSettingToFullSetting = <T extends SettingType_Enum>({
  settingType,
  setting,
}: {
  settingType: T;
  setting: OptionalDeep<Setting>;
}): Setting | undefined => {
  const settingKey = convertSettingTypeToKey(settingType);
  if (!settingKey) return undefined;
  const typedSetting =
    settingKey in setting ? (setting[settingKey as keyof typeof setting] as unknown as SettingTypeMap[T]) : undefined;

  switch (settingType) {
    case SettingType_Enum.TEST_SETTING:
      return {
        testSetting: {
          enabled: false,
          values: [],
          ...typedSetting,
        },
      } satisfies Setting;
    case SettingType_Enum.INBOX_USER_SETTING:
      return {
        inboxUserSetting: {
          notificationSound: '',
          ...typedSetting,
        },
      } satisfies Setting;
    case SettingType_Enum.TEST_NO_DEFAULT_SETTING:
      return {
        testNoDefaultSetting: {
          enabled: false,
          values: [],
          ...typedSetting,
        },
      };
    case SettingType_Enum.INBOX_LOCATION_SETTING:
      return {
        inboxLocationSetting: {
          showUnrepliedStatus: false,
          processConfirmationsQuietly: false,
          processConfirmationsSilently: false,
          processBulkMessagesSilently: false,
          moveBulkTextMessagesToTopOfInboxAfterSend: false,
          processAutoMessagesSilently: false,
          enableActionableTab: false,
          ...typedSetting,
        },
      } satisfies Setting;
    case SettingType_Enum.MUTE_INBOX_LOCATION_SETTING:
      return {
        muteInboxLocationSetting: {
          muted: false,
          ...typedSetting,
        },
      } satisfies Setting;
    case SettingType_Enum.AUTO_MESSAGING_LOCATION_SETTING:
      return {
        autoMessagingLocationSetting: {
          replyToEmail: '',
          ...typedSetting,
        },
      };
    case SettingType_Enum.INBOX_LOCATION_USER_SETTING:
      return {
        inboxLocationUserSetting: {
          enableMessageNotifications: false,
          enableMessageNotificationsForConfirmations: false,
          ...typedSetting,
        },
      };
    case SettingType_Enum.REPUTATION_USER_SETTING:
      return {
        reputationUserSetting: {
          notificationSound: '',
          ...typedSetting,
        },
      };
    case SettingType_Enum.REPUTATION_NOTIFICATIONS_LOCATION_USER_SETTING:
      return {
        reputationNotificationsLocationUserSetting: {
          ...DEFAULT_SETTINGS.reputationNotificationsLocationUserSetting,
          googleReviewSettings: {
            ...DEFAULT_SETTINGS.reputationNotificationsLocationUserSetting.googleReviewSettings,
            ...(typedSetting && 'googleReviewSettings' in typedSetting ? typedSetting.googleReviewSettings : {}),
          },
          facebookReviewSettings: {
            ...DEFAULT_SETTINGS.reputationNotificationsLocationUserSetting.facebookReviewSettings,
            ...(typedSetting && 'facebookReviewSettings' in typedSetting ? typedSetting.facebookReviewSettings : {}),
          },
          privateReviewSettings: {
            ...DEFAULT_SETTINGS.reputationNotificationsLocationUserSetting.privateReviewSettings,
            ...(typedSetting && 'privateReviewSettings' in typedSetting ? typedSetting.privateReviewSettings : {}),
          },
          textSurveySettings: {
            ...DEFAULT_SETTINGS.reputationNotificationsLocationUserSetting.textSurveySettings,
            ...(typedSetting && 'textSurveySettings' in typedSetting ? typedSetting.textSurveySettings : {}),
          },
        },
      };
    case SettingType_Enum.WEAVE_ASSISTANT_LOCATION_SETTING:
      return {
        weaveAssistantLocationSetting: {
          ...DEFAULT_SETTINGS.weaveAssistantLocationSetting,
          ...typedSetting,
        },
      };
    case SettingType_Enum.AI_RECEPTIONIST_LOCATION_USER_SETTINGS:
      return {
        aiReceptionistLocationUserSettings: {
          ...DEFAULT_SETTINGS.aiReceptionistLocationUserSettings,
          ...typedSetting,
        },
      };
    case SettingType_Enum.BRAND_SETTING:
      return {
        brandSetting: {
          ...DEFAULT_SETTINGS.brandSetting,
          ...typedSetting,
        },
      };
    default:
      return undefined;
  }
};
